import * as t from "io-ts";
import { NumberFromString } from "io-ts-types/lib/NumberFromString";

import { StreamValue } from "../../models/streamfield-blocks";
import { codecFromEnum } from "../../models/utils";

export type GridVariant = "mattress" | "pillows" | "streamfield";

export enum GridFilterSelectionMode {
    SELECT_ONE = "one",
    SELECT_MANY = "many",
}

export enum PriceComparator {
    LT = "LT",
    LTE = "LTE",
    GT = "GT",
    GTE = "GTE",
}

export enum SortDirection {
    ASC = "ASC",
    DESC = "DESC",
}

const GridFilterSelectionModeCodec = codecFromEnum(
    "GridFilterSelectionMode",
    GridFilterSelectionMode,
);
const PriceComparatorCodec = codecFromEnum("PriceComparator", PriceComparator);
const SortDirectionCodec = codecFromEnum("SortDirection", SortDirection);

const SingleBoundPriceRange = StreamValue(
    "single_bound_price_range",
    t.readonly(
        t.type({
            id: t.string,
            comparator: PriceComparatorCodec,
            amount: NumberFromString,
        }),
    ),
);
export type ISingleBoundPriceRange = t.TypeOf<typeof SingleBoundPriceRange>;

const DualBoundPriceRange = StreamValue(
    "dual_bound_price_range",
    t.readonly(
        t.type({
            id: t.string,
            lower_bound: t.readonly(
                t.type({
                    comparator: PriceComparatorCodec,
                    amount: NumberFromString,
                }),
            ),
            upper_bound: t.readonly(
                t.type({
                    comparator: PriceComparatorCodec,
                    amount: NumberFromString,
                }),
            ),
        }),
    ),
);
export type IDualBoundPriceRange = t.TypeOf<typeof DualBoundPriceRange>;

const PriceRange = t.union([SingleBoundPriceRange, DualBoundPriceRange]);
export type IPriceRange = t.TypeOf<typeof PriceRange>;

const BooleanAttribute = t.readonly(
    t.type({
        attribute: t.string,
        label: t.string,
    }),
);
export type IBooleanAttribute = t.TypeOf<typeof BooleanAttribute>;

const AttributeValueGridFilter = StreamValue(
    "attribute_value_grid_filter",
    t.readonly(
        t.type({
            filter_id: t.string,
            label: t.string,
            selection_mode: GridFilterSelectionModeCodec,
        }),
    ),
);

const ProductClassGridFilter = StreamValue(
    "product_class_grid_filter",
    t.readonly(
        t.type({
            filter_id: t.string,
            label: t.string,
            selection_mode: GridFilterSelectionModeCodec,
        }),
    ),
);

const PriceRangeGridFilter = StreamValue(
    "price_range_grid_filter",
    t.readonly(
        t.type({
            filter_id: t.string,
            label: t.string,
            selection_mode: GridFilterSelectionModeCodec,
            ranges: t.readonlyArray(PriceRange),
        }),
    ),
);

const BooleanAttributeGridFilter = StreamValue(
    "boolean_attribute_grid_filter",
    t.readonly(
        t.type({
            filter_id: t.string,
            label: t.string,
            selection_mode: GridFilterSelectionModeCodec,
            attributes: t.readonlyArray(BooleanAttribute),
        }),
    ),
);

const AttributeValueGridSorter = StreamValue(
    "attribute_value_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
            attribute: t.string,
        }),
    ),
);

const PriceGridSorter = StreamValue(
    "price_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
        }),
    ),
);

const PillowsPriceGridSorter = StreamValue(
    "pillows_price_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
        }),
    ),
);

const RatingGridSorter = StreamValue(
    "rating_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
        }),
    ),
);

const RatingNumberGridSorter = StreamValue(
    "rating_number_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
        }),
    ),
);

const DateCreatedGridSorter = StreamValue(
    "date_created_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
        }),
    ),
);

const GridFilter = t.union([
    AttributeValueGridFilter,
    ProductClassGridFilter,
    PriceRangeGridFilter,
    BooleanAttributeGridFilter,
]);

const GridSorter = t.union([
    AttributeValueGridSorter,
    PriceGridSorter,
    PillowsPriceGridSorter,
    RatingGridSorter,
    RatingNumberGridSorter,
    DateCreatedGridSorter,
]);

export const GridFilters = t.readonlyArray(GridFilter);
export const GridSorters = t.readonlyArray(GridSorter);

export type IProductCardType = "grid-card" | "sub-category-card-grid";

export type IGridFilterOptions = Readonly<{
    filter_id: string;
    label: string;
    selection_mode?: GridFilterSelectionMode;
}>;

export type IGridSorterConfigOptions = Readonly<{
    id: string;
    label: string;
    direction: SortDirection;
}>;

export type IFilterOption = Readonly<{
    id: string;
    label: string;
}>;
